import { useCallback } from 'react'
import { useStore } from 'react-redux'
import type { NavigateOptions } from 'react-router-dom'

import { useDraftWorkspace } from '@/drafting/workspace/hooks/useDraftWorkspace'
import type { MatterArtifactVersionRead } from '@/matter-artifacts/schemas'
import { selectCurrentMatterArtifactVersionByArtifactId, selectOpenMatterArtifactId } from '@/matter-artifacts/store/selectors'
import type { RootState } from '@/store/store'

/** Opens a matter artifact from a conversation. */
export function useOpenMatterArtifact(onOpenExternal?: (versionId: string) => void) {
  const store = useStore<RootState>()
  const { draftArtifactVersionId, openDraft } = useDraftWorkspace()

  return useCallback(
    (matterArtifactVersion: MatterArtifactVersionRead, navigateOptions?: NavigateOptions) => {
      const state = store.getState()
      const matterArtifactId = matterArtifactVersion.matter_artifact_id
      // Reopening what is already on screen churns the URL and remounts the draft.
      if (!onOpenExternal && selectOpenMatterArtifactId(state, { draftArtifactVersionId }) === matterArtifactId) return

      // Buttons carry the version they were generated for, so open the current one once hydration has it.
      const versionToOpen = selectCurrentMatterArtifactVersionByArtifactId(state, matterArtifactId) ?? matterArtifactVersion

      if (onOpenExternal) onOpenExternal(versionToOpen.id)
      else openDraft(versionToOpen.id, navigateOptions)
    },
    [draftArtifactVersionId, onOpenExternal, openDraft, store]
  )
}
